import React, { useState } from 'react';
import styles from './TemplateRow.module.scss';
import classNames from 'classnames';
import Menu from '../atoms/Menu';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { getFullName } from '../../utils/dashboardUtils';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import { getNumOfRoutineExercises } from '../../utils/dashboardUtils';
import ContentCopy from '@mui/icons-material/ContentCopy';

import type { TrainingRoutineTemplate, Trainer } from '../../types';

interface TemplateRowProps {
  template: TrainingRoutineTemplate;
  selected: boolean;
  onSelect?: () => void;
  trainer: Trainer | undefined;
  openEditTemplateModal?: () => void;
  duplicateTemplate?: (template: TrainingRoutineTemplate) => void;
  deleteTemplate?: (template: TrainingRoutineTemplate) => Promise<void>;
  allowDelete: boolean;
}

const TemplateRow: React.FC<TemplateRowProps> = ({
  template,
  trainer,
  onSelect,
  selected,
  deleteTemplate,
  duplicateTemplate,
  openEditTemplateModal,
  allowDelete,
}) => {
  const [
    isDeleteTemplateConfirmationOpen,
    setIsDeleteTemplateConfirmationOpen,
  ] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleCloseMenuOptions = () => {
    setAnchorEl(null);
  };

  const getMenuItems = () => {
    const menuItems: Array<{
      onClick: () => void;
      label: string;
      icon: React.ReactNode;
    }> = [];
    if (openEditTemplateModal) {
      menuItems.push({
        onClick: () => {
          handleCloseMenuOptions();
          openEditTemplateModal();
        },
        label: 'Edit Workout Details',
        icon: <BorderColorOutlinedIcon color="primary" />,
      });
      menuItems.push({
        onClick: () => {
          handleCloseMenuOptions();
          duplicateTemplate?.(template);
        },
        label: 'Duplicate Workout',
        icon: <ContentCopy color="primary" />,
      });
    }
    if (deleteTemplate && allowDelete) {
      menuItems.push({
        onClick: () => {
          handleCloseMenuOptions();
          setIsDeleteTemplateConfirmationOpen(true);
        },
        label: 'Delete Workout',
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      });
    }
    return menuItems;
  };

  const handleDeleteTemplate = async () => {
    handleCloseMenuOptions();
    await deleteTemplate?.(template);
    setIsDeleteTemplateConfirmationOpen(false);
  };

  const renderTemplateName = () => (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (openEditTemplateModal) {
          openEditTemplateModal();
        }
      }}
      className={classNames(styles.templateColumn, styles.nameAndDescription)}
    >
      <div className={styles.templateName}>{template.name}</div>
      {template.routine.description && (
        <div className={styles.templateDescription}>
          {template.routine.description}
        </div>
      )}
    </div>
  );
  const renderExercises = () => (
    <div className={styles.templateColumn}>
      {getNumOfRoutineExercises(template.routine)}
    </div>
  );

  const renderTrainerName = () => {
    const trainerName = getFullName(trainer);
    return (
      <div className={classNames(styles.templateColumn)}>{trainerName}</div>
    );
  };

  // Add a helper function to format the date as DD/MM/YY
  const formatLastEditDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const renderLastEdit = () => (
    <div className={styles.templateColumn}>
      {formatLastEditDate(template.updatedDate)}
    </div>
  );

  const renderDeleteTemplateConfirmationDialog = () => (
    <ConfirmationDialog
      text="Are you sure you want to delete this workout?"
      onSubmit={handleDeleteTemplate}
      submitText="Yes"
      cancelText="No"
      isOpen={isDeleteTemplateConfirmationOpen}
      onCancel={() => setIsDeleteTemplateConfirmationOpen(false)}
      symbol={<DeleteForeverOutlinedIcon />}
    />
  );

  const renderMoreOptions = () => (
    <div className={classNames(styles.templateColumn, styles.options)}>
      <PendingOutlinedIcon
        className={styles.optionsBtn}
        color="primary"
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget as unknown as HTMLElement);
        }}
      />
      <Menu
        anchorEl={anchorEl}
        isOpen={open}
        onClose={handleCloseMenuOptions}
        options={getMenuItems()}
      />
    </div>
  );

  const handleRowClick = () => {
    // Don't trigger row selection if menu is open
    // Let the menu close naturally when clicking away
    if (!open && onSelect) {
      onSelect();
    }
  };

  return (
    <>
      <div
        onClick={handleRowClick}
        className={classNames(styles.templateRow, styles.listGrid, {
          [styles.selected]: selected,
        })}
      >
        {renderTemplateName()}
        {renderExercises()}
        {renderTrainerName()}
        {renderLastEdit()}
        {renderMoreOptions()}
      </div>
      {renderDeleteTemplateConfirmationDialog()}
    </>
  );
};

export default TemplateRow;
